import Link from 'next/link';
import { Box, Typography } from '@wishket/design-system';
import { twJoin, twMerge } from 'tailwind-merge';

import ArticleInfo from './ArticleInfo';
import ArticleAuthor from './ArticleAuthor';
import ArticleItemImage from './ArticleItemImage';

import { ArticleItemProps } from '@/shared/types';
import { PATH } from '@/shared/constants';

export interface ArticleColumnItemProps extends ArticleItemProps {
  isMainArticle?: boolean;
  isMainTrendingArticle?: boolean;
  hasDescription?: boolean;
  isSimplified?: boolean;
}

const ArticleColumnItem = ({
  href,
  title,
  category,
  categoryHref,
  description,
  thumbnail,
  articleInfo,
  author,
  isMainArticle,
  hasDescription,
  isSimplified = false,
  isMainTrendingArticle = false,
}: ArticleColumnItemProps) => {
  return (
    <article
      data-testid="article-column-item--container"
      className={twJoin(
        'group flex w-[210px] shrink-0 flex-col gap-4 overflow-y-visible tablet:w-full',
        isMainArticle && 'w-full desktop:gap-8'
      )}
    >
      <Link href={href}>
        <Box
          data-testid="article-column-item--image"
          className={twJoin(
            'flex-none duration-200 ease-in',
            'desktop:group-hover:-translate-y-2'
          )}
        >
          <ArticleItemImage
            src={
              thumbnail ||
              `${PATH.MEDIA_YOZM_IMAGE}/default_thumbnail_article.png`
            }
            alt={title}
            width={0}
            height={0}
            sizes="1000px"
            className={twMerge(
              'aspect-video',
              'w-full rounded-2xl border border-w-gray-100 object-cover',
              isMainArticle && 'rounded-3xl',
              isMainTrendingArticle && 'rounded-3xl'
            )}
            priority
            loading="eager"
          />
        </Box>
      </Link>
      <Box
        data-testid="article-column-item--info"
        className={twMerge(
          `flex flex-col gap-3`,
          isMainArticle && 'desktop:gap-4',
          isMainTrendingArticle && 'gap-2'
        )}
      >
        <Box
          className={twMerge(
            `flex flex-col gap-1.5`,
            isMainArticle && 'desktop:gap-2'
          )}
        >
          <Link href={categoryHref} data-testid="contentsItem-category-link">
            <Typography
              variant="body2"
              className="text-w-gray-500 hover:underline"
            >
              {category}
            </Typography>
          </Link>
          <Box
            data-testid="article-column-item--title"
            className={twMerge(
              'flex flex-col gap-1.5',
              isMainArticle && 'gap-2',
              isMainTrendingArticle && 'gap-1'
            )}
          >
            <Link href={href} data-testid="contentsItem-item-link">
              <Typography
                as="h3"
                variant="body1"
                className={twJoin(
                  isMainArticle
                    ? 'tablet:typo-subTitle1 desktop:typo-title3'
                    : 'tablet:typo-subTitle2',
                  'line-clamp-2 break-all font-medium text-w-gray-900 group-hover:text-w-gray-600'
                )}
              >
                {title}
              </Typography>
            </Link>
            <ArticleInfo {...articleInfo} />
            {hasDescription && (
              <Link href={href}>
                <Box
                  className="hidden tablet:block"
                  data-testid="contentsItem-description"
                >
                  <Typography
                    variant="body2"
                    className="line-clamp-2 text-w-gray-500"
                  >
                    {description}
                  </Typography>
                </Box>
              </Link>
            )}
          </Box>
        </Box>
        {!isSimplified && <ArticleAuthor {...author} />}
      </Box>
    </article>
  );
};

export default ArticleColumnItem;
